import { useEffect, useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Eye, Flag, Target, Bell, Droplets, Building2, User, Sprout, Heart, Bike, Star, Mountain, Tent,
  Footprints, Sailboat, Cloud, Activity, Waves, MountainSnow
} from 'lucide-react';
import { aboutAPI } from '../services/api';
import { contentAPI } from '../services/api';
import { useSiteSettings } from '../hooks/useContent';
import { usePageSeo } from '../hooks/usePageSeo';
import { buildOrganizationSchema, buildBreadcrumbSchema } from '../utils/seo';
import { useAuth } from '../context/AuthContext';
import { popDocumentScrollLock, pushDocumentScrollLock } from '../utils/documentScrollLock';
import LoadingIndicator from '../components/LoadingIndicator';
import { optimizeImageUrl, IMAGE_DISPLAY_WIDTH } from '../utils/optimizeImageUrl.js';
import './Blogs.css';
import './About.css';


const defaultAbout = {
  hero: {
    headline: "India's Largest Trekking Organisation",
    description:
      'Since our inception, we have been dedicated to empowering youth through immersive adventure experiences, fostering a deep connection with nature, and building a community of responsible leaders.',
    imageUrl: '',
    title: 'About Invincible',
    subtitle: 'Adventure Awaits, Community Thrives'
  },
  vision: {
    title: 'Our Vision',
    text:
      'To be the global beacon for youth adventure, inspiring a generation to embrace sustainable living, courage, and selfless service to society and nature.',
    icon: 'eye'
  },
  mission: {
    title: 'Our Mission',
    text:
      'To provide accessible, world-class trekking experiences that educate young minds about environmental conservation while building character, resilience, and teamwork.',
    icon: 'flag'
  },
  objective: {
    title: 'Our Objective',
    text:
      "To organize over 1,000 expeditions annually, engage one million youth in adventure sports, and lead the nation's largest volunteer-driven nature conservation drives.",
    icon: 'target'
  },
  sectionTitles: {
    projects: 'Our Projects',
    projectsSubtitle: '',
    activities: 'Activities We Do',
    journey: 'Our Journey'
  },
  cta: {
    teamText: 'Meet Our Team',
    teamLink: '/team',
    volunteerText: 'Join Invincible As Volunteer',
    volunteerLink: '/careers'
  },
  projects: [
    { id: 'proj-nature', title: 'Nature Education', icon: 'mountain' },
    { id: 'proj-river', title: 'River Cleaning', icon: 'drop' },
    { id: 'proj-heritage', title: 'Heritage Walks', icon: 'building' },
    { id: 'proj-rural', title: 'Rural Dev.', icon: 'shovel' },
    { id: 'proj-empower', title: 'Empowerment', icon: 'person' },
    { id: 'proj-clean', title: 'Clean Drives', icon: 'drop' },
    { id: 'proj-plant', title: 'Plantation', icon: 'shovel' },
    { id: 'proj-blood', title: 'Blood Donation', icon: 'blood' },
    { id: 'proj-cycle', title: 'Cycling Club', icon: 'cycle' },
    { id: 'proj-leader', title: 'Leadership', icon: 'star' }
  ],
  activities: [
    { id: 'act-trek', title: 'Trekking', icon: 'mountain' },
    { id: 'act-camp', title: 'Camping', icon: 'tent' },
    { id: 'act-raft', title: 'River Rafting', icon: 'raft' },
    { id: 'act-para', title: 'Paragliding', icon: 'parachute' },
    { id: 'act-climb', title: 'Rock Climbing', icon: 'climb' },
    { id: 'act-dive', title: 'Scuba Diving', icon: 'dive' },
    { id: 'act-cave', title: 'Caving', icon: 'cave' },
    { id: 'act-ski', title: 'Skiing', icon: 'ski' }
  ],
  journey: [
    {
      id: 'journey-2013',
      year: '2013',
      title: 'Inception',
      description: 'Founded with a vision to empower youth through adventure.'
    },
    {
      id: 'journey-2015',
      year: '2015',
      title: '10k Members',
      description: 'Crossed the landmark of 10,000 registered trekkers.'
    },
    {
      id: 'journey-2018',
      year: '2018',
      title: 'National Award',
      description: 'Recognized nationally for youth development.'
    },
    {
      id: 'journey-2021',
      year: '2021',
      title: 'Largest Drive',
      description: 'Organized the largest clean-up drive in Himalayas.'
    },
    {
      id: 'journey-2024',
      year: '2024',
      title: 'Global Reach',
      description: 'Expanding expeditions to international peaks.'
    }
  ]
};

const ICON_COMPONENTS = {
  eye: Eye,
  flag: Flag,
  target: Target,
  bell: Bell,
  drop: Droplets,
  building: Building2,
  person: User,
  shovel: Sprout,
  blood: Heart,
  cycle: Bike,
  star: Star,
  mountain: Mountain,
  tent: Tent,
  hike: Footprints,
  raft: Sailboat,
  parachute: Cloud,
  climb: Activity,
  dive: Waves,
  cave: Mountain,
  ski: MountainSnow
};

function AboutIcon({ iconKey, size = 32, className = '' }) {
  const Icon = iconKey ? ICON_COMPONENTS[iconKey] : null;
  if (!Icon) return null;
  return <Icon size={size} strokeWidth={1.8} className={className} />;
}

function splitGallery(gallery) {
  if (!gallery) return [];
  return String(gallery)
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function projectStats(project) {
  if (!project || typeof project !== 'object') return [];
  const rows = [
    { label: project.stat1Label ?? project.stat1label, value: project.stat1Value ?? project.stat1value },
    { label: project.stat2Label ?? project.stat2label, value: project.stat2Value ?? project.stat2value },
    { label: project.stat3Label ?? project.stat3label, value: project.stat3Value ?? project.stat3value }
  ];
  return rows.filter((s) => s.value != null && String(s.value).trim() !== '');
}

const About = () => {
  const { user } = useAuth();
  const { settings } = useSiteSettings();
  const [data, setData] = useState(defaultAbout);
  const [loading, setLoading] = useState(true);
  const [expandedProjectId, setExpandedProjectId] = useState(null);
  const [volunteerModalOpen, setVolunteerModalOpen] = useState(false);
  const [volunteerForm, setVolunteerForm] = useState({ name: '', phone: '' });
  const [volunteerSubmitting, setVolunteerSubmitting] = useState(false);
  const [volunteerError, setVolunteerError] = useState('');
  const [volunteerSuccess, setVolunteerSuccess] = useState('');
  const didAutoOpenFirstProjectRef = useRef(false);

  const publicBase =
    String(settings.site_public_url || '').replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const brand = settings.site_brand_name || 'Invincible NGO';
  const aboutTitle =
    String(settings.about_hero_title || '').trim() ||
    data.hero?.title ||
    'About Us';
  const aboutDesc =
    String(settings.about_hero_description || '').trim() ||
    data.hero?.description ||
    settings.site_meta_description;
  const aboutSchema = useMemo(() => {
    const org = buildOrganizationSchema(settings);
    const breadcrumbs = buildBreadcrumbSchema([
      { name: 'About Us', url: '/about' }
    ], publicBase);
    return {
      '@context': 'https://schema.org',
      '@graph': [org, breadcrumbs]
    };
  }, [settings, publicBase]);

  usePageSeo({
    title: `${aboutTitle} | ${brand}`,
    description: aboutDesc,
    canonical: publicBase ? `${publicBase}/about` : undefined,
    schema: aboutSchema
  });


  useEffect(() => {
    let cancelled = false;
    aboutAPI
      .getAbout()
      .then((res) => {
        const payload = res.data?.data || res.data;
        if (payload && !cancelled) {
          setData({
            ...defaultAbout,
            ...payload,
            hero: { ...defaultAbout.hero, ...(payload.hero || {}) },
            sectionTitles: { ...defaultAbout.sectionTitles, ...(payload.sectionTitles || {}) }
          });
        }
      })
      .catch(() => {
        if (!cancelled) setData(defaultAbout);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fromUser = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    setVolunteerForm((prev) => ({
      name: prev.name || fromUser,
      phone: prev.phone || String(user?.phone || '').trim()
    }));
  }, [user?.firstName, user?.lastName, user?.phone]);

  useEffect(() => {
    if (!volunteerModalOpen) return undefined;
    pushDocumentScrollLock();
    return () => popDocumentScrollLock();
  }, [volunteerModalOpen]);

  const { hero, vision, mission, objective, sectionTitles, cta, projects, activities, journey } = data;

  const projectsSectionSubtitle = String(sectionTitles?.projectsSubtitle || '').trim();

  const heroTitle =
    String(hero?.title || hero?.headline || '').trim() || 'About Invincible';
  const heroSubtitle = String(hero?.subtitle || '').trim();
  const heroDescription = String(hero?.description || '').trim();
  const heroImageUrl = String(
    hero?.imageUrl ?? hero?.imageURL ?? hero?.image_url ?? hero?.about_hero_image ?? ''
  ).trim();

  const toggleProject = (id) => {
    setExpandedProjectId((prev) => (prev === id ? prev : id));
  };

  const openVolunteerModal = () => {
    setVolunteerError('');
    setVolunteerSuccess('');
    setVolunteerModalOpen(true);
  };

  const closeVolunteerModal = () => {
    if (volunteerSubmitting) return;
    setVolunteerModalOpen(false);
  };

  const handleVolunteerSubmit = async (e) => {
    e.preventDefault();
    setVolunteerError('');
    setVolunteerSuccess('');
    const name = String(volunteerForm.name || '').trim();
    const phone = String(volunteerForm.phone || '').trim();
    const digits = phone.replace(/\D/g, '');
    if (!name) {
      setVolunteerError('Please enter your name.');
      return;
    }
    if (digits.length !== 10) {
      setVolunteerError('Please enter a valid 10-digit phone number.');
      return;
    }
    setVolunteerSubmitting(true);
    try {
      await contentAPI.submitVolunteerInquiry({ name, phone: digits });
      setVolunteerSuccess('Thanks! We have received your volunteer request.');
      setVolunteerForm((prev) => ({ ...prev, phone: digits }));
      window.setTimeout(() => {
        setVolunteerModalOpen(false);
        setVolunteerSuccess('');
      }, 900);
    } catch (err) {
      setVolunteerError(err?.response?.data?.message || 'Could not submit right now. Please try again.');
    } finally {
      setVolunteerSubmitting(false);
    }
  };

  const expandedProject =
    expandedProjectId != null
      ? projects.find((x) => String(x.id) === String(expandedProjectId))
      : null;

  const expandedStats = expandedProject ? projectStats(expandedProject) : [];
  const expandedGalleryUrls = expandedProject ? splitGallery(expandedProject.gallery) : [];
  const expandedLinkUrl = expandedProject ? String(expandedProject.link || '').trim() : '';
  const expandedBody = expandedProject
    ? String(expandedProject.detail || expandedProject.description || '').trim()
    : '';

  useEffect(() => {
    if (!projects.length) return;
    if (!didAutoOpenFirstProjectRef.current) {
      setExpandedProjectId(projects[0].id);
      didAutoOpenFirstProjectRef.current = true;
      return;
    }
    if (expandedProjectId != null) {
      const stillExists = projects.some((p) => String(p.id) === String(expandedProjectId));
      if (!stillExists) setExpandedProjectId(projects[0].id);
    }
  }, [projects, expandedProjectId]);

  if (loading) {
    return (
      <div className="about-page-root">
        <div className="blog-hero-banner">
          <div className="blog-hero-inner">
            <LoadingIndicator label="Loading..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="about-page-root">
      {/* Same hero pattern as Special Events (Expeditions): orange band + white title */}
      <div className="about-page-shell">
        <section className="blog-hero-banner about-page-hero-band">
          <div className="blog-hero-inner">
            <h1 className="blog-hero-title">{heroTitle}</h1>
            {heroSubtitle ? <p className="blog-hero-subtitle">{heroSubtitle}</p> : null}
          </div>
        </section>
      </div>

      {/* Intro paragraph — plain, left-aligned copy (no hero image block) */}
      {heroDescription ? (
        <div className="about-intro-plain">
          <p className="about-intro-plain-text">{heroDescription}</p>
        </div>
      ) : null}

      <div className="about-page">
        <section className="about-vmo-section">
          <div className="about-vmo-grid">
            <div className="about-vmo-card">
              <div className="about-vmo-icon">
                <AboutIcon iconKey={vision.icon} size={40} />
              </div>
              <h2 className="about-vmo-title">{vision.title}</h2>
              <p className="about-vmo-text">{vision.text}</p>
            </div>
            <div className="about-vmo-card">
              <div className="about-vmo-icon">
                <AboutIcon iconKey={mission.icon} size={40} />
              </div>
              <h2 className="about-vmo-title">{mission.title}</h2>
              <p className="about-vmo-text">{mission.text}</p>
            </div>
            <div className="about-vmo-card">
              <div className="about-vmo-icon">
                <AboutIcon iconKey={objective.icon} size={40} />
              </div>
              <h2 className="about-vmo-title">{objective.title}</h2>
              <p className="about-vmo-text">{objective.text}</p>
            </div>
          </div>
        </section>

        {projects.length > 0 && (
          <section className="about-section">
            <div
              className={`about-projects-section-header${projectsSectionSubtitle ? ' about-projects-section-header--has-subtitle' : ''}`}
            >
              <h2 className="section-title about-section-title-underline">{sectionTitles.projects}</h2>
              {projectsSectionSubtitle ? (
                <p className="about-section-subtitle">{projectsSectionSubtitle}</p>
              ) : null}
            </div>
            <div className="about-projects-button-grid">
              {projects.map((p) => {
                const isOpen = expandedProjectId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`about-project-tile ${isOpen ? 'about-project-tile--open' : ''}`}
                    onClick={() => toggleProject(p.id)}
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? `about-project-panel-${p.id}` : undefined}
                    aria-label={
                      isOpen
                        ? `Collapse details for ${p.title}`
                        : `Expand details for ${p.title}`
                    }
                  >
                    <span className="about-project-tile-title">{p.title}</span>
                  </button>
                );
              })}
            </div>

            {expandedProject ? (
              <div className="about-project-expanded" id={`about-project-panel-${expandedProject.id}`}>
                <h3 className="about-project-expanded-title">{expandedProject.title}</h3>
                {expandedBody ? <p className="about-project-expanded-desc">{expandedBody}</p> : null}

                {(expandedStats.length > 0 || expandedLinkUrl) && (
                  <div className="about-project-expanded-stats-row">
                    {expandedStats.length > 0 ? (
                      <div className="about-project-expanded-stats">
                        {expandedStats.map((s, idx) => (
                          <div key={idx} className="about-project-stat">
                            <span className="about-project-stat-value">{s.value}</span>
                            {s.label ? <span className="about-project-stat-label"> {s.label}</span> : null}
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {expandedLinkUrl ? (
                      <a
                        href={expandedLinkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="about-project-expanded-know-more"
                      >
                        Know More
                        <img src="/assets/arrow-up-right.svg" alt="" aria-hidden="true" />
                      </a>
                    ) : null}
                  </div>
                )}

                {expandedGalleryUrls.length > 0 ? (
                  <div className="about-project-gallery">
                    <div className="about-project-gallery-asymmetric">
                      <div className="about-project-gallery-main">
                        <img
                          src={optimizeImageUrl(expandedGalleryUrls[0], { width: IMAGE_DISPLAY_WIDTH.gallery })}
                          alt=""
                          loading="lazy"
                        />
                      </div>
                      <div className="about-project-gallery-side">
                        {expandedGalleryUrls.slice(1, 5).map((url, gi) => (
                          <div key={gi} className="about-project-gallery-sub">
                            <img
                              src={optimizeImageUrl(url, { width: IMAGE_DISPLAY_WIDTH.card })}
                              alt=""
                              loading="lazy"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>
        )}

        {activities.length > 0 && (
          <section className="about-section">
            <h2 className="section-title about-section-title-underline">{sectionTitles.activities}</h2>
            <div className="about-activities-grid">
              {activities.map((a) => (
                <div key={a.id} className="about-activity-card">
                  <div className="about-activity-icon">
                    <AboutIcon iconKey={a.icon} size={36} />
                  </div>
                  <h3>{a.title}</h3>
                </div>
              ))}
            </div>
          </section>
        )}

        {journey.length > 0 && (
          <section className="about-section">
            <h2 className="section-title about-section-title-underline">{sectionTitles.journey}</h2>
            <div className="about-journey-timeline">
              {journey.map((j) => (
                <div key={j.id} className="about-journey-item">
                  <div className="about-journey-dot" />
                  <div className="about-journey-content">
                    <span className="about-journey-year">{j.year}</span>
                    <h3 className="about-journey-item-title">{j.title}</h3>
                    <p className="about-journey-desc">{j.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="about-cta-section">
          <Link to={cta.teamLink || '/team'} className="btn-secondary">
            {cta.teamText || 'Meet Our Team'}
          </Link>
          <button type="button" className="btn-primary about-volunteer-open-btn" onClick={openVolunteerModal}>
            {cta.volunteerText || 'Join Invincible as Volunteer!'}
          </button>
        </section>

        {volunteerModalOpen ? (
          <div className="about-volunteer-modal-overlay" role="dialog" aria-modal="true" aria-label="Volunteer form">
            <button
              type="button"
              className="about-volunteer-modal-backdrop"
              onClick={closeVolunteerModal}
              aria-label="Close volunteer form"
            />
            <div className="about-volunteer-modal-card">
              <div className="about-volunteer-modal-head">
                <h3>Join as Volunteer</h3>
                <button type="button" className="about-volunteer-modal-close modal-close-circle" onClick={closeVolunteerModal} aria-label="Close">
                  ×
                </button>
              </div>
              <form className="about-volunteer-form" onSubmit={handleVolunteerSubmit}>
                <label htmlFor="volunteer-name">Name</label>
                <input
                  id="volunteer-name"
                  type="text"
                  value={volunteerForm.name}
                  onChange={(e) => setVolunteerForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Your full name"
                  required
                />
                <label htmlFor="volunteer-phone">Phone Number</label>
                <input
                  id="volunteer-phone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={14}
                  value={volunteerForm.phone}
                  onChange={(e) => setVolunteerForm((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="10-digit mobile number"
                  required
                />
                {volunteerError ? <p className="about-volunteer-error">{volunteerError}</p> : null}
                {volunteerSuccess ? <p className="about-volunteer-success">{volunteerSuccess}</p> : null}
                <button type="submit" className="about-volunteer-submit" disabled={volunteerSubmitting}>
                  {volunteerSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </form>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default About;
